import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface TimeTrackingTask {
  id: string;
  title: string;
  estimated_minutes: number;
  actual_duration_minutes: number;
  scheduled_date: string;
  is_tracking: boolean;
}

const TimeTracking: React.FC = () => {
  const [tasks, setTasks] = useState<TimeTrackingTask[]>([]);
  const [summary, setSummary] = useState({ totalEstimatedMinutes: 0, totalActualMinutes: 0, accuracy: 0 });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeTracking, setActiveTracking] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTimeTrackingSummary(dateRange.startDate, dateRange.endDate);
      if (response.success) {
        setTasks(response.tasks || []);
        setSummary(response.summary || { totalEstimatedMinutes: 0, totalActualMinutes: 0, accuracy: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch time tracking:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const handleStartTracking = async (taskId: string) => {
    try {
      await apiService.startTimeTracking(taskId);
      setActiveTracking(taskId);
      fetchData();
    } catch (error) {
      console.error('Failed to start tracking:', error);
      alert('Failed to start tracking');
    }
  };

  const handleStopTracking = async (taskId: string) => {
    try {
      await apiService.stopTimeTracking(taskId);
      setActiveTracking(null);
      fetchData();
    } catch (error) {
      console.error('Failed to stop tracking:', error);
      alert('Failed to stop tracking');
    }
  };

  const formatMinutes = (minutes: number) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    return `${hours}h ${mins}m`;
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 90 && accuracy <= 110) return '#10b981';
    if (accuracy >= 70 && accuracy <= 130) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div style={{ padding: '24px' }} data-testid="time-tracking">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Time Tracking</h2>
        <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Track actual vs planned time on tasks</p>
      </div>

      {/* Date Range Selector */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#6b7280' }}>From</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#6b7280' }}>To</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#0369a1' }}>Total Estimated</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#0c4a6e' }}>{formatMinutes(summary.totalEstimatedMinutes)}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#15803d' }}>Total Tracked</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#14532d' }}>{formatMinutes(summary.totalActualMinutes)}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fefce8', borderRadius: '12px', border: '1px solid #fef08a' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#a16207' }}>Estimation Accuracy</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: getAccuracyColor(summary.accuracy) }}>{summary.accuracy}%</p>
        </div>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: '#6b7280' }}>No tracked tasks in this period. Start tracking time on tasks from the planner!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tasks.map(task => (
            <div
              key={task.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                gap: '16px'
              }}
            >
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>{task.title}</h4>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                  {new Date(task.scheduled_date).toLocaleDateString()}
                </p>
              </div>
              <div style={{ textAlign: 'center', minWidth: '80px' }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Estimated</p>
                <p style={{ margin: 0, fontWeight: 600, color: '#3b82f6' }}>{formatMinutes(task.estimated_minutes)}</p>
              </div>
              <div style={{ textAlign: 'center', minWidth: '80px' }}>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Actual</p>
                <p style={{ margin: 0, fontWeight: 600, color: '#10b981' }}>{formatMinutes(task.actual_duration_minutes)}</p>
              </div>
              <button
                onClick={() => task.is_tracking || activeTracking === task.id ? handleStopTracking(task.id) : handleStartTracking(task.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: task.is_tracking || activeTracking === task.id ? '#fee2e2' : '#dbeafe',
                  color: task.is_tracking || activeTracking === task.id ? '#dc2626' : '#1d4ed8',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                {task.is_tracking || activeTracking === task.id ? 'Stop' : 'Start'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f0f4ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#4338ca' }}>
          <strong>Tip:</strong> Track time on tasks to improve your estimation skills. Tasks tracked on the planner will appear here automatically.
        </p>
      </div>
    </div>
  );
};

export default TimeTracking;
